import React, { useState } from "react";
import { trustSkipped, dismissSkipped } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { MailX, Globe, AtSign, X, Loader2, Paperclip, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

export const SkippedDigest = ({ items, total, onChanged }) => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  const trust = async (it, scope) => {
    setBusy(it.message_id + scope);
    try {
      const res = await trustSkipped(it.message_id, scope, user);
      toast.success(`Trusted ${res.trusted}${res.created?.length ? ` — ${res.created.length} invoice(s) ingested` : ""}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not trust sender");
    }
    setBusy(null);
  };

  const dismiss = async (it) => {
    await dismissSkipped(it.message_id);
    onChanged();
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="skipped-digest">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border text-sm font-medium">
        <span className="flex items-center gap-2"><MailX className="h-4 w-4 text-amber-500" /> Skipped senders this week
          <span className="font-mono text-xs text-muted-foreground" data-testid="skipped-digest-count">{items.length}</span></span>
        {total > items.length && <span className="text-[11px] text-muted-foreground">{total} unresolved in total</span>}
        <button data-testid="skipped-digest-manage" onClick={() => navigate("/upload")} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">Manage <ArrowUpRight className="h-3 w-3" /></button>
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-5 text-xs text-muted-foreground italic" data-testid="skipped-digest-empty">No emails were held back this week — every sender was trusted.</div>
      ) : (
        <ul className="divide-y divide-border max-h-64 overflow-auto d20-scroll">
          {items.map((it) => (
            <li key={it.message_id} className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-2.5" data-testid={`skipped-digest-row-${it.message_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{it.from || it.from_email}</div>
                <div className="text-xs text-muted-foreground truncate">{it.subject || "(no subject)"}
                  {it.attachments?.length > 0 && <span className="ml-2 inline-flex items-center gap-1"><Paperclip className="h-3 w-3" />{it.attachments.length}</span>}
                  <span className="ml-2">{new Date(it.skipped_at).toLocaleDateString()}</span></div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button data-testid="skipped-digest-trust-domain" size="sm" variant="outline" disabled={!!busy} onClick={() => trust(it, "domain")}>
                  {busy === it.message_id + "domain" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1" />} @{it.from_domain}
                </Button>
                <Button data-testid="skipped-digest-trust-email" size="sm" variant="ghost" disabled={!!busy} onClick={() => trust(it, "email")} title="Trust this address only"><AtSign className="h-3.5 w-3.5" /></Button>
                <button data-testid="skipped-digest-dismiss" className="text-muted-foreground hover:text-foreground p-1" onClick={() => dismiss(it)}><X className="h-4 w-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
